import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocale } from '../context/LocaleContext';
import SafeIcon from '../common/SafeIcon';

export default function CuteConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText, 
  cancelText, 
  icon = '🥺', 
  theme = 'danger', // 'danger' | 'neutral'
  hideCancel = false // 新增屬性：隱藏取消按鈕，變成純警告視窗
}) {
  const { t } = useLocale();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const displayTitle = title || t('confirm.title');
  const displayMessage = message || t('confirm.message');
  const displayConfirm = confirmText || t('confirm.confirm');
  const displayCancel = cancelText || t('confirm.cancel');

  const isDanger = theme === 'danger';
  const iconBgClass = isDanger 
    ? 'bg-red-50 dark:bg-red-900/20 text-red-500 border-red-100 dark:border-red-900/50' 
    : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700';
    
  const confirmBtnClass = isDanger
    ? 'bg-gradient-to-r from-red-400 to-rose-500 text-white hover:from-red-500 hover:to-rose-600 shadow-red-200'
    : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 shadow-gray-200';

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 sm:px-0">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white dark:bg-slate-800 w-full max-w-[320px] rounded-[2rem] shadow-2xl relative z-10 flex flex-col overflow-hidden border-4 border-white dark:border-slate-700"
          >
            <div className="p-6 text-center">
              <div className={`w-16 h-16 rounded-[1.2rem] flex items-center justify-center mx-auto mb-4 text-3xl rotate-3 shadow-sm border ${iconBgClass}`}>
                {icon}
              </div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{displayTitle}</h3>
              <p className="text-gray-500 dark:text-gray-400 font-medium text-sm leading-relaxed">
                {displayMessage}
              </p>
            </div>
            
            <div className="flex bg-gray-50 dark:bg-slate-900 p-3 gap-3 border-t border-gray-100 dark:border-slate-700">
              {!hideCancel && (
                <button 
                  onClick={onClose}
                  className="flex-1 py-3.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white rounded-xl font-bold hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors shadow-sm"
                >
                  {displayCancel}
                </button>
              )}
              <button 
                onClick={() => {
                  if (onConfirm) onConfirm();
                  if (hideCancel && !onConfirm) onClose(); // 警告視窗模式下，點擊確認直接關閉
                }}
                className={`flex-1 py-3.5 rounded-xl font-bold shadow-lg transition-all active:scale-95 dark:shadow-none ${confirmBtnClass}`}
              >
                {displayConfirm}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}